import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, photoUrl } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { CalendarDays, ImageIcon, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Timeline() {
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(undefined);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null); // {analysis, from_date, to_date, entry_count}

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/entries");
        setEntries(data || []);
      } catch (err) {
        console.error("Failed to load entries:", err);
        toast.error("Could not load your entries");
      }
    })();
  }, []);

  const sev = (v) => v <= 3 ? "bg-emerald-200 text-emerald-900" : v <= 6 ? "bg-amber-200 text-amber-900" : "bg-rose-200 text-rose-900";
  const entryDates = entries.map(e => new Date(e.entry_date + "T00:00"));
  const filteredEntries = selected
    ? entries.filter(e => e.entry_date === selected.toISOString().slice(0, 10))
    : entries;

  const runAnalysis = async () => {
    if (entries.length < 2) {
      toast.error("Log at least two entries before analysing a trend");
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const { data } = await api.post("/timeline/analyze", {});
      setAnalysis(data);
      toast.success("Trend analysis complete");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not analyse trend");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-sage-500">Your journey</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900 mt-2">Timeline</h1>
        </div>
        <Button
          onClick={runAnalysis}
          disabled={analyzing || entries.length < 2}
          className="rounded-full bg-clay-500 hover:bg-clay-400 text-white"
          data-testid="timeline-analyze-btn"
        >
          {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Sparkles className="w-4 h-4 mr-2"/>}
          {analyzing ? "Analysing trend…" : "Trend analysis"}
        </Button>
      </div>

      {analysis && (
        <div className="mt-6 bg-white rounded-2xl p-6 border border-stone-100 animate-fade-up" data-testid="timeline-analysis-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-clay-400"/>
              <h3 className="font-serif text-2xl text-stone-900">Trend analysis</h3>
            </div>
            <p className="text-xs text-stone-500">
              {analysis.entry_count} entries · {analysis.from_date} → {analysis.to_date}
            </p>
          </div>
          <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900 leading-relaxed">
            <strong>This is not medical advice.</strong> AI output is informational only and may be inaccurate. Always consult a qualified dermatologist for any concerns.
          </div>
          <pre className="mt-4 whitespace-pre-wrap text-sm text-stone-700 font-sans leading-relaxed" data-testid="timeline-analysis-text">{analysis.analysis}</pre>
          <button onClick={() => setAnalysis(null)} className="mt-4 text-xs text-stone-500 hover:text-stone-700 underline" data-testid="timeline-analysis-close">
            Dismiss
          </button>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <aside className="bg-white rounded-2xl p-4 border border-stone-100" data-testid="calendar-card">
          <div className="flex items-center gap-2 px-2 pt-2"><CalendarDays className="w-4 h-4 text-sage-400"/><h3 className="font-serif text-xl text-stone-900">Calendar</h3></div>
          <Calendar mode="single" selected={selected} onSelect={setSelected}
            modifiers={{ logged: entryDates }}
            modifiersClassNames={{ logged: "bg-sage-100 text-sage-700 font-semibold" }}
            className="mt-2"/>
          {selected && <button onClick={() => setSelected(undefined)} className="text-xs text-sage-500 hover:underline mt-3 ml-2" data-testid="clear-filter">Clear filter</button>}
        </aside>

        <div className="lg:col-span-2 space-y-4" data-testid="timeline-list">
          {filteredEntries.length === 0 && (
            <div className="bg-white rounded-2xl p-10 text-center border border-stone-100">
              <p className="text-stone-500">{selected ? "No entry on this date." : "No entries yet — log your first day to begin."}</p>
            </div>
          )}
          {filteredEntries.map((e, idx) => (
            <Link to={`/entry/${e.id}`} key={e.id} className="block bg-white rounded-2xl p-5 border border-stone-100 hover:-translate-y-0.5 transition-all duration-300 animate-fade-up" style={{ animationDelay: `${idx * 60}ms` }} data-testid={`timeline-entry-${e.id}`}>
              <div className="flex gap-5 items-start">
                {e.photo_paths?.[0] ? (
                  <img src={photoUrl(e.photo_paths[0])} alt="" className="w-24 h-24 rounded-xl object-cover"/>
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-sand-200 flex items-center justify-center text-sage-500"><ImageIcon className="w-7 h-7"/></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-serif text-xl text-stone-900">{e.entry_date}</span>
                    <span className="text-xs uppercase tracking-widest text-stone-400">·</span>
                    <span className="text-sm text-stone-500">{(e.body_regions || []).join(", ")}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(e.enabled_symptoms || ["itching", "pain", "redness"]).map((s) => {
                      const labels = { itching: "Itch", pain: "Pain", redness: "Red", dryness: "Dry", weeping: "Weep" };
                      const v = e[s] || 0;
                      return <Badge key={s} className={`${sev(v)} border-0 rounded-full font-mono text-xs`}>{labels[s]} {v}</Badge>;
                    })}
                  </div>
                  {e.notes && <p className="mt-3 text-sm text-stone-500 line-clamp-2">{e.notes}</p>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
